import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { length } from 'redux-form-validators';
import { filter, isEmpty, toPlainObject } from 'lodash';

import {
    FIRSTNAME_LENGTH,
    NAME_LENGTH,
    ADDRESS_LENGTH,
    POSTCODE_LENGTH,
    CITY_LENGTH,
    PHONE_LENGTH
} from '../../../constants/form';
import {
    KYC_STATE_DECLINED,
    KYC_STATE_RETRY,
    KYC_STATE_VALIDATED
} from '../../../services/kyc/constants';
import { PATH } from '../../../constants/path';
import { CONTEXT_ROOT, STATIC_ROOT } from '../../../constants';
import { MONSIEUR, MADAME } from '../../../constants/civilities';
import { FRAUDSTER_TOOL_SMS, FRAUDSTER_TOOL_KYC } from '../../../constants/fraud';
import { ENTERPRISE_CARD_NAME, ENTERPRISE_CARD_PARTNER_EBC_ID } from '../../../constants/ebon';

import {
    createOrder,
    lockEbons,
    resetLock,
    sendCheckSms,
    checkConfirmationSms,
    setPaymentType
} from '../../../services/ebon/actions';
import modalService from '../../../services/modal';
import { showModal } from '../../../services/modal/actions';
import { logoutUser } from '../../../services/authentication/logout/actions';
import { fetchUserBalance } from '../../../services/account/balance/actions';
import { payWithEnterpriseCard } from '../../../services/enterprisecard/actions';
import { initializeKyc, fetchKycState, resetKyc } from '../../../services/kyc/actions';

import {
    formatAmountCurrency,
    getPartnerIdFromQuery,
    getPartnerNameFromQuery,
    mapCountries
} from '../../../utils';
import { mergeQueryLocked } from '../../../utils/ebonUtils';
import { storeLastUserInfo } from '../../../utils/storageUtils';
import { isTokenExpired } from '../../../services/authentication/utils';
import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';
import { getMerchantCardUrl, convertPartnerNameForUrl } from '../../../services/partner/utils';

import {
    FormComponent,
    LegendFieldComponent,
    NumberInputFieldComponent,
    PhoneInputFieldComponent,
    SelectFieldComponent,
    TextInputFieldComponent,
    CheckboxInputFieldComponent
} from '../../form';
import Stepper from './Stepper';
import { Logo } from '../../icons/Logo';
import SvgComponent from '../../SvgComponent';
import HeadComponent from '../../HeadComponent';
import SmsTooltip from './sms/tooltip/SmsTooltip';
import CheckoutIframe from '../../iframes/checkout';
import ButtonComponent from '../../button/ButtonComponent';
import LocalizedNavLink from '../../router/LocalizedNavLink';
import PaymentSelectorComponent from './PaymentSelectorComponent';
import { LoadingComponent } from '../../loading/LoadingComponent';

import './ValidationEbonPage.scss';

const STEP_1 = 'address';
const STEP_2 = 'payment_choice';
const STEP_3 = 'fraud_verification';
const STEP_4 = 'payment';

const mapUserCart = (cart, partners) => {
    if (cart.length > 0) {
        const partner = filter(partners, p => p.id === cart[0].partnerId)[0];
        const isEnterpriseCard = partner && partner.ebcId === ENTERPRISE_CARD_PARTNER_EBC_ID;
        const partnerName = isEnterpriseCard ? ENTERPRISE_CARD_NAME : partner.name;
        return cart.map(item => {
            return {
                quantity: item.quantityQuery,
                partnerName: partnerName,
                value: item.facialValue
            };
        });
    }
    return [];
};

const mapPartner = (cart, partners) => {
    if (cart.length > 0) {
        return filter(partners, p => p.id === cart[0].partnerId)[0];
    }
    return undefined;
};

const FORM_NAME = 'ebonbilling-form';

const ValidationEbonPage = ({
    i18n,
    BRAND_NAME,
    ebon,
    lock,
    lockFetching,
    lockFailure,
    confirmationSmsStatus,
    smsToken,
    userCart,
    order,
    createOrder,
    createOrderRequest,
    enterprisecardSubmiting,
    checkoutAllowed,
    lockEbons,
    resetLock,
    sendCheckSms,
    checkConfirmationSms,
    form,
    token,
    isAuthenticated,
    user,
    history,
    showModal,
    logout,
    CIVILITIES,
    COUNTRIES,
    i18nCommon,
    initialValues,
    partner,
    queryPartnerId,
    quaryPartnerName,
    initializeKyc,
    fetchKycState,
    kycWidgetLink,
    kycState,
    resetKyc,
    paymentType,
    setPaymentType,
    payWithEnterpriseCard,
    fetchUserBalance
}) => {
    const [userInfo, setUserInfo] = useState({});
    const [step, setStep] = useState(STEP_1);
    const [payment, setPayment] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [sms, setSms] = useState();
    const [kyc, setKyc] = useState();
    const [cappingError, setCappingError] = useState(false);

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    const partnerId = partner ? partner.id : queryPartnerId;
    const isEnterpriseCard = partner && partner.ebcId === ENTERPRISE_CARD_PARTNER_EBC_ID;
    const partnerName = isEnterpriseCard
        ? ENTERPRISE_CARD_NAME
        : partner
        ? partner.name
        : quaryPartnerName;
    const merchantCardPath = getMerchantCardUrl({ id: partnerId, name: partnerName }, false, true);

    const isFetching = lockFetching || createOrderRequest || enterprisecardSubmiting;

    const {
        ebonDiscount = { cashback: { fixed: 0, percent: 0 }, parity: { fixed: 0, percent: 0 } }
    } = ebon.ebons;
    const fixed = ebonDiscount.cashback.value;
    const percent = ebonDiscount.cashback.percent.value;
    const amountCashback = fixed > 0 ? fixed : percent;

    const doCartTotalCashbackAmount = cart => {
        const cartTotal = cart.reduce((acc, curr) => acc + curr.quantity * curr.value, 0);
        const cashback = (cartTotal * (100 - amountCashback)) / 100;
        return Number((cartTotal - cashback).toFixed(2));
    };

    const checkAuthentication = () => {
        if (!isMemberAuthenticated() || userCart.length === 0) {
            new Promise((resolve, reject) => {
                history.push(merchantCardPath);
                resolve();
            }).then(() => logout(() => showModal(modalService.constants.MODAL_TYPE_LOGIN)));
        }
    };
    useEffect(() => {
        checkAuthentication();
    });

    useEffect(() => {
        fetchUserBalance();
    }, []);

    const returnToSelection = urlParams => {
        resetLock();
        resetKyc();
        setPaymentType(null);

        const url = `${merchantCardPath}${urlParams ? urlParams : ''}`;
        history.push(url);
    };

    // Set user info and step
    const handleSubmit = async () => {
        const formData = form[FORM_NAME].values;

        let number = formData.phoneNumber;
        if (!number.startsWith('+')) {
            number = `+33${number.startsWith('0') ? number.substring(1, number.length) : number}`;
        }

        const tempUserInfo = {
            lastname: formData.lastname,
            firstname: formData.firstname,
            billingAdressLine1: formData.address,
            billingAdressLine2: formData.address,
            billingAdress: formData.address,
            billingCity: formData.city,
            billingPostalCode: formData.zipcode,
            billingCountry: formData.country,
            phoneNumber: number,
            saveAddress: formData.saveAddress
        };

        storeLastUserInfo(formData.firstname, formData.lastname, number);

        setUserInfo(tempUserInfo);
        setStep(STEP_2);
    };

    // Do lock
    const handlePaymentChoice = () => {
        setPayment(paymentType);

        const ebonCart = {
            lockRequests: ebon.cart,
            userId: user.username,
            partnerId: partnerId,
            fixedFacialValue: true
        };

        lockEbons(ebonCart, userInfo.phoneNumber);
    };

    // Handle lock response
    useEffect(() => {
        if (!isEmpty(lock)) {
            const { allLocked } = lock;
            if (!allLocked) {
                delete lock.allLocked;
                const final = mergeQueryLocked(ebon.cart, lock);
                return setModalData(final);
            }

            // Get fraudster tools
            const fraudsterProbability =
                lock[0].fraudsterProbability && lock[0].fraudsterProbability.suspected === true;
            const fraudsterByPassing =
                lock[0].fraudsterProbability &&
                lock[0].fraudsterProbability.bypassing &&
                lock[0].fraudsterProbability.bypassing.includes('CHECKOUT');
            const fraudsterTools = fraudsterProbability && lock[0].fraudsterProbability.tools;

            // Use appropriate fraudster tool
            if (
                fraudsterProbability &&
                !fraudsterByPassing &&
                fraudsterTools.includes(FRAUDSTER_TOOL_KYC)
            ) {
                const kycRequest = {
                    firstname: userInfo.firstname,
                    lastname: userInfo.lastname,
                    mobileNumber: userInfo.phoneNumber
                };
                setKyc(true);
                setStep(STEP_3);
                initializeKyc(kycRequest);
            } else if (
                fraudsterProbability &&
                !fraudsterByPassing &&
                fraudsterTools.includes(FRAUDSTER_TOOL_SMS)
            ) {
                setSms(true);
                setStep(STEP_3);
                sendCheckSms(userInfo.phoneNumber, partnerId);
            } else {
                handleCreateOrder();
                setStep(STEP_4);
            }
        }
    }, [lock]);
    useEffect(() => {
        if (!isEmpty(lockFailure)) {
            // Check user annual capping
            if (lockFailure.statusCode === 406) {
                setCappingError(true);
            }
        }
    }, [lockFailure]);

    // Create order
    const handleCreateOrder = () => {
        const cartAmount = userCart.reduce(
            (previousValue, currentValue) =>
                previousValue + currentValue.quantity * currentValue.value,
            0
        );
        const requestedAmount = payment === 'enterpriseCard' ? cartAmount : null;

        createOrder(partnerId, userInfo.phoneNumber, requestedAmount);
    };

    // Handle order (for enterprise card)
    useEffect(() => {
        if (payment === 'enterpriseCard' && order && order.orderState === 'created') {
            payWithEnterpriseCard(userInfo, partnerId, order.id);
        }
    }, [order]);

    /* Start SMS verification */
    useEffect(() => {
        if (smsToken) startCheckingConfirmationSms(smsToken);
    }, [smsToken]);

    const startCheckingConfirmationSms = token => {
        checkConfirmationSms(token);
    };

    useEffect(() => {
        if (confirmationSmsStatus && confirmationSmsStatus > 0) {
            const interval = setInterval(() => {
                checkConfirmationSms(smsToken);
                if (confirmationSmsStatus === 403) {
                    setSms(false);
                    setPaymentType(null);
                    resetLock();
                    setStep(STEP_1);
                    clearInterval(interval);
                    history.push(merchantCardPath);
                }
                if (confirmationSmsStatus === 204) {
                    handleCreateOrder();
                    clearInterval(interval);
                    setSms(false);
                }
            }, 3000);
            return () => {
                clearInterval(interval);
            };
        }
    }, [confirmationSmsStatus]);
    /* End SMS verification */

    /* Start KYC verification */
    useEffect(() => {
        if (kycWidgetLink) {
            startCheckingKycState();
            window.open(kycWidgetLink, '_blank');
        }
    }, [kycWidgetLink]);

    const startCheckingKycState = () => {
        fetchKycState();
    };

    useEffect(() => {
        if (kyc && kycState !== null) {
            const interval = setInterval(() => {
                fetchKycState();
                if (kycState === KYC_STATE_DECLINED || kycState === KYC_STATE_RETRY) {
                    setKyc(false);
                    setPaymentType(null);
                    resetLock();
                    resetKyc();
                    setStep(STEP_1);
                    clearInterval(interval);
                    history.push(merchantCardPath);
                }
                if (kycState === KYC_STATE_VALIDATED) {
                    handleCreateOrder();
                    clearInterval(interval);
                    setKyc(false);
                    setStep(STEP_4);
                }
            }, 3000);
            return () => {
                clearInterval(interval);
            };
        }
    }, [kycState]);
    /* End KYC verification */

    const isFraudVerificationActive = () => {
        return sms || kyc;
    };

    return (
        <div className="template template-primary flex-container-v">
            <div className="entete hideNav" id="entete">
                <div className="flex-container-v">
                    <header className="tetedepage conteneur flex-container-h flex-center-alt">
                        <LocalizedNavLink to={PATH.HOME_PATH} title={BRAND_NAME} className="logo">
                            <Logo />
                        </LocalizedNavLink>

                        <Stepper
                            steps={i18n.STEPS}
                            current={order.orderState === 'created' || step !== STEP_1 ? 2 : 1}
                        />
                    </header>
                </div>
            </div>

            <main id="p-ebon-validation" className="conteneur conteneur-mobile">
                <HeadComponent
                    title={i18n.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${
                        PATH.EBON_VALIDATION_PATH
                    }/${convertPartnerNameForUrl(partnerName)}-${partnerId}`}
                />
                <Stepper
                    steps={i18n.STEPS}
                    current={order.orderState === 'created' || step !== STEP_1 ? 2 : 1}
                    cssClass="mobileDisplay"
                />
                <div className="goBack">
                    <p className="fakelink" onClick={() => returnToSelection()}>
                        <SvgComponent src={`${STATIC_ROOT}/chevron.svg`} width="12" height="12" />
                        {i18n.GO_BACK}
                    </p>
                </div>

                {!modalData && (
                    <div className="mainContainer">
                        <div className="leftContainer">
                            <div className="leftInnerContainer">
                                {order.orderState === 'created' &&
                                lock.allLocked &&
                                step === STEP_4 &&
                                !isFraudVerificationActive() &&
                                payment === 'creditCard' &&
                                checkoutAllowed ? (
                                    <CheckoutIframe
                                        userInfo={userInfo}
                                        partnerId={partnerId}
                                        order={order}
                                    />
                                ) : step === STEP_1 && !isFraudVerificationActive() ? (
                                    <React.Fragment>
                                        <h3>{i18n.BILLING_ADDRESS}</h3>
                                        <FormComponent
                                            form={FORM_NAME}
                                            initialValues={initialValues}
                                            classNameInputContainer="grid-2 has-gutter"
                                            requestSubmit={isFetching}
                                            onSubmit={() => handleSubmit()}
                                            submitText={i18nCommon.VALIDATE_USER_DETAILS}
                                            paymentLockLabel={i18nCommon.PAYMENT_LOCKPAD_LABEL}>
                                            <LegendFieldComponent classNameInputContainer="full">
                                                {i18nCommon.WELCOME_TITLE}
                                            </LegendFieldComponent>
                                            <SelectFieldComponent
                                                name="civility"
                                                label={i18nCommon.LABEL_CIVILITY}
                                                isRequired
                                                defaultOptionLabel="--"
                                                classNameInputContainer="full"
                                                options={[
                                                    { label: CIVILITIES.MONSIEUR, value: MONSIEUR },
                                                    { label: CIVILITIES.MADAME, value: MADAME }
                                                ]}
                                            />
                                            <TextInputFieldComponent
                                                name="lastname"
                                                placeholder={i18nCommon.LABEL_LASTNAME}
                                                label={i18nCommon.LABEL_LASTNAME}
                                                isRequired
                                                validate={[
                                                    length({
                                                        max: NAME_LENGTH,
                                                        msg: format(
                                                            i18nCommon.TEXT_TOOLONG,
                                                            NAME_LENGTH,
                                                            NAME_LENGTH > 1 ? 's' : ''
                                                        )
                                                    })
                                                ]}
                                            />
                                            <TextInputFieldComponent
                                                name="firstname"
                                                placeholder={i18nCommon.LABEL_FIRSTNAME}
                                                label={i18nCommon.LABEL_FIRSTNAME}
                                                isRequired
                                                validate={[
                                                    length({
                                                        max: FIRSTNAME_LENGTH,
                                                        msg: format(
                                                            i18nCommon.TEXT_TOOLONG,
                                                            FIRSTNAME_LENGTH,
                                                            FIRSTNAME_LENGTH > 1 ? 's' : ''
                                                        )
                                                    })
                                                ]}
                                            />
                                            <TextInputFieldComponent
                                                name="address"
                                                classNameInputContainer="full"
                                                placeholder={i18nCommon.LABEL_ADDRESS}
                                                label={i18nCommon.LABEL_ADDRESS}
                                                isRequired
                                                validate={[
                                                    length({
                                                        max: ADDRESS_LENGTH,
                                                        msg: i18nCommon.ADDRESS_SIZE_ERROR
                                                    })
                                                ]}
                                            />
                                            <NumberInputFieldComponent
                                                name="zipcode"
                                                classNameInputContainer="one-half"
                                                placeholder={i18nCommon.LABEL_ZIPCODE}
                                                label={i18nCommon.LABEL_ZIPCODE}
                                                isRequired
                                                validate={[
                                                    length({
                                                        max: POSTCODE_LENGTH,
                                                        msg: i18nCommon.ZIP_CODE_SIZE_ERROR
                                                    })
                                                ]}
                                            />
                                            <TextInputFieldComponent
                                                name="city"
                                                classNameInputContainer="one-half"
                                                placeholder={i18nCommon.LABEL_CITY}
                                                label={i18nCommon.LABEL_CITY}
                                                isRequired
                                                validate={[
                                                    length({
                                                        max: CITY_LENGTH,
                                                        msg: i18nCommon.CITY_SIZE_ERROR
                                                    })
                                                ]}
                                            />
                                            <SelectFieldComponent
                                                name="country"
                                                label={i18nCommon.LABEL_COUNTRY}
                                                isRequired
                                                defaultOptionLabel={i18nCommon.LABEL_COUNTRY}
                                                defaultValue="FRANCE"
                                                classNameInputContainer="full"
                                                options={COUNTRIES}
                                            />
                                            <PhoneInputFieldComponent
                                                name="phoneNumber"
                                                label={i18nCommon.LABEL_PHONENUMBER}
                                                labelInfo={
                                                    <SmsTooltip
                                                        text={i18n.PHONE_NUMBER_INFO}
                                                        right
                                                        isHover>
                                                        {i18n.PHONE_NUMBER_LABEL_INFO}
                                                    </SmsTooltip>
                                                }
                                                classNameInputContainer="full"
                                                isRequired
                                                validate={[
                                                    length({
                                                        max: PHONE_LENGTH,
                                                        msg: i18nCommon.PHONENUMBER_SIZE_ERROR
                                                    })
                                                ]}
                                            />
                                            <TextInputFieldComponent
                                                name="company"
                                                label={i18nCommon.LABEL_COMPANY_COMITY}
                                                classNameInputContainer="full"
                                            />
                                            <CheckboxInputFieldComponent
                                                name="saveAddress"
                                                options={[
                                                    {
                                                        label: i18nCommon.LABEL_SAVE_ADDRESS,
                                                        value: true
                                                    }
                                                ]}
                                                classNameInputContainer="full"
                                            />
                                        </FormComponent>
                                    </React.Fragment>
                                ) : step === STEP_3 && sms ? (
                                    <div className="smsWaiting">
                                        <SvgComponent
                                            src={`${STATIC_ROOT}/payment/tools.svg`}
                                            width="80"
                                            height="100"
                                        />
                                        <p>{i18n.SMS_VALIDATION.TEXT_1}</p>
                                        <span>{i18n.SMS_VALIDATION.TEXT_2}</span>
                                    </div>
                                ) : (
                                    step === STEP_3 &&
                                    kyc && (
                                        <div className="kycWaiting">
                                            <p>
                                                {convertHtmlToReactDomWithParams(
                                                    i18n.KYC.WAITING_MODAL.TEXT_1
                                                )}
                                            </p>
                                            <p>
                                                {convertHtmlToReactDomWithParams(
                                                    i18n.KYC.WAITING_MODAL.TEXT_2,
                                                    kycWidgetLink
                                                )}
                                            </p>
                                            <p>{i18n.KYC.WAITING_MODAL.TEXT_3}</p>
                                            <p>
                                                {convertHtmlToReactDomWithParams(
                                                    i18n.KYC.WAITING_MODAL.TEXT_4
                                                )}
                                            </p>
                                            <p>
                                                {convertHtmlToReactDomWithParams(
                                                    i18n.KYC.WAITING_MODAL.TEXT_5
                                                )}
                                            </p>
                                            <p className="bold">{i18n.KYC.WAITING_MODAL.TEXT_6}</p>
                                        </div>
                                    )
                                )}

                                {step === STEP_2 &&
                                    (!isFetching ? (
                                        <React.Fragment>
                                            <h3>{`${i18n.PAYMENT_METHOD}`}</h3>
                                            <PaymentSelectorComponent
                                                cart={userCart}
                                                isEnterpriseCard={isEnterpriseCard}
                                            />
                                        </React.Fragment>
                                    ) : (
                                        <div className="loading-order">
                                            <LoadingComponent />
                                        </div>
                                    ))}

                                {step === STEP_4 && isFetching && (
                                    <div className="loading-order">
                                        <LoadingComponent />
                                    </div>
                                )}

                                {cappingError && (
                                    <p className="error-message">
                                        {convertHtmlToReactDomWithParams(
                                            i18n.CAPPING_ERROR_MESSAGE_1
                                        )}
                                        <span
                                            className="fakeLink"
                                            onClick={() => returnToSelection('#conditions')}>
                                            {i18n.CAPPING_ERROR_MESSAGE_2}
                                        </span>
                                        {i18n.CAPPING_ERROR_MESSAGE_3}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="rightContainer">
                            <h3>{`${i18n.RECAPITULATIF}`}</h3>
                            <div className="rightInnerContainer bordered ebonRecap">
                                <h3>{`${i18n.BASKET}`}</h3>
                                {userCart.map((ebons, i) => (
                                    <div key={i} className="align">
                                        <p>
                                            {convertHtmlToReactDomWithParams(
                                                i18n.BON_ACHAT_LIGNE,
                                                ebons.partnerName,
                                                formatAmountCurrency(ebons.value)
                                            )}
                                        </p>
                                        <div style={{ minWidth: '110px' }}>
                                            <span
                                                style={{
                                                    justifyContent: 'flex-end',
                                                    flex: 2
                                                }}>
                                                x {ebons.quantity}
                                            </span>{' '}
                                            <span> = </span>{' '}
                                            <span style={{ justifyContent: 'flex-end', flex: 2 }}>
                                                {formatAmountCurrency(ebons.quantity * ebons.value)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="rightInnerContainer bordered-no-top ebonTotal">
                                <div className="shiping">
                                    <p>{`${i18n.LIVRAISON}`}</p>
                                    <div>
                                        <span>{`${i18n.GRATUITE}`}</span>
                                    </div>
                                </div>
                                <div className="row-text">
                                    <p>{convertHtmlToReactDomWithParams(i18n.EBON_CAGNOTTE)}</p>
                                    <div>
                                        <span>
                                            {formatAmountCurrency(
                                                doCartTotalCashbackAmount(userCart)
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <div className="total">
                                    <p>{`${i18n.TOTAL_VALUE}`}</p>
                                    <div>
                                        <span>
                                            {formatAmountCurrency(
                                                userCart
                                                    .map(e => e.quantity * e.value)
                                                    .reduce((a, b) => a + b, 0)
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {(step === STEP_2 ||
                                (step === STEP_4 && payment === 'enterpriseCard')) && (
                                <React.Fragment>
                                    <ButtonComponent
                                        color="main"
                                        text={i18n.BUTTON_TEXT}
                                        onClick={handlePaymentChoice}
                                        disabled={
                                            !paymentType ||
                                            isFetching ||
                                            (step === STEP_4 && payment === 'enterpriseCard')
                                        }
                                    />
                                    <div className="lock-label-override">
                                        <SvgComponent
                                            src={`${STATIC_ROOT}/lockpad.svg`}
                                            width="14"
                                            height="14"
                                        />
                                        <span className="lockpad-label">
                                            {i18n.PAYMENT_LOCKPAD_LABEL}
                                        </span>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                )}

                {modalData && (
                    <div className="modaldata-container">
                        <h1>{i18n.MODALDATA.TITLE}</h1>
                        <p className="recapText">{i18n.MODALDATA.TEXT_1}</p>
                        <h3>{`${i18n.RECAPITULATIF}`}</h3>
                        <div className="rightInnerContainer bordered">
                            {modalData.map((ebons, id) => (
                                <div key={id} className="recapUnavailable">
                                    <p>
                                        {convertHtmlToReactDomWithParams(
                                            i18n.BON_ACHAT_LIGNE,
                                            partnerName,
                                            formatAmountCurrency(ebons.facialValue)
                                        )}
                                    </p>
                                    <div style={{ minWidth: '100px' }}>
                                        <span>
                                            {ebons.quantityLocked}/{ebons.quantityQuery}{' '}
                                        </span>
                                        <span>{i18n.MODALDATA.TEXT_2}</span>
                                        <span style={{ justifyContent: 'flex-end', flex: 2 }}>
                                            {formatAmountCurrency(
                                                ebons.quantityLocked * ebons.facialValue
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ))}

                            <ButtonComponent
                                onClick={() => returnToSelection()}
                                color="main"
                                text={i18n.MODALDATA.BUTTON}
                            />
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

ValidationEbonPage.propTypes = {
    token: PropTypes.string,
    isAuthenticated: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired
};

const mapStateToProps = (state, ownProps) => {
    const user = toPlainObject(state.account.user);
    const queryPartnerId = getPartnerIdFromQuery(ownProps.match.params.query);
    const quaryPartnerName = getPartnerNameFromQuery(ownProps.match.params.query);

    return {
        i18n: state.i18n.page.EBON,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated,
        user: user,
        ebon: state.ebon,
        lock: state.ebon.lock,
        confirmationSmsStatus: state.ebon.confirmationSms,
        smsToken: state.ebon.tokenSms,
        lockFetching: state.ebon.lockFetching,
        lockFailure: state.ebon.lockFailure,
        order: state.ebon.createdOrder,
        createOrderRequest: state.ebon.createOrderRequest,
        enterprisecardSubmiting: state.enterprisecard.fetching,
        form: state.form,
        BRAND_NAME: state.i18n.common.BRAND_NAME,
        CIVILITIES: state.i18n.feature.form.CIVILITIES,
        COUNTRIES: mapCountries(state.i18n.feature.form.COUNTRIES),
        i18nCommon: state.i18n.feature.form,
        userCart: mapUserCart(state.ebon.cart, state.ebon.ebonPartners),
        logout: logoutUser,
        partner: mapPartner(state.ebon.cart, state.ebon.ebonPartners),
        queryPartnerId: queryPartnerId,
        quaryPartnerName: quaryPartnerName,
        paymentType: state.ebon.paymentType,
        initialValues: {
            civility: user.civility ? user.civility : '',
            lastname: user.lastname ? user.lastname : '',
            firstname: user.firstname ? user.firstname : '',
            address: user.address ? user.address : '',
            zipcode: user.postcode ? user.postcode : '',
            city: user.city ? user.city : '',
            country: user.country ? user.country : '',
            phoneNumber: user.mobileNumber ? user.mobileNumber : ''
        },
        checkoutAllowed:
            state.ebon.ebons.ebonPaymentConfig &&
            state.ebon.ebons.ebonPaymentConfig.checkoutAllowed,
        kycWidgetLink: state.kyc.widgetLink,
        kycState: state.kyc.state
    };
};

const mapDispatchToProps = {
    showModal,
    lockEbons,
    sendCheckSms,
    checkConfirmationSms,
    initializeKyc,
    fetchKycState,
    resetKyc,
    resetLock,
    createOrder,
    setPaymentType,
    payWithEnterpriseCard,
    fetchUserBalance
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ValidationEbonPage));



// WEBPACK FOOTER //
// ./src/components/pages/ebon/ValidationEbonPage.js